import { Box, Flex, useMediaQuery } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useTranslate } from "@tolgee/react";
import { colors } from "../../constants/GlobalStyles";
import {
  ChatSidePanelProps,
  PAGE_HEADER_HEIGHT,
  PAGE_HEADER_HEIGHT_MOBILE,
  SidePanelTab,
} from "../../constants/GlobalTypesAndEnumsAndConstants";
import { askQuestionsChatStore, pageStore, userStore } from "../../zustand/globalStore";
import { isDesktop, isMobile } from "../../backend/utils/detect-device";
import { IconCancelX } from "../../icons";
import { useChatQuotedReferenceStore } from "./chat-tab/chatQuotedReference";
import monkeyFace from "../../voice/assets/monkey-face.png";

const pulse = keyframes`0%, 80%, 100% { opacity: .25; transform: translateY(0) } 40% { opacity: 1; transform: translateY(-2px) }`;
const shimmer = keyframes`0% { opacity: .55 } 50% { opacity: 1 } 100% { opacity: .55 }`;

/** A quoted passage ("> …" lines) and the question under it, drawn like the chat's own user bubble. */
function PendingUserMessage({ text }: { text: string }) {
  const blocks: { quote: boolean; lines: string[] }[] = [];
  for (const line of text.split("\n")) {
    const quote = line.startsWith(">");
    const content = quote ? line.replace(/^>\s?/u, "") : line;
    const last = blocks[blocks.length - 1];
    if (last && last.quote === quote) last.lines.push(content);
    else if (content.trim() || quote) blocks.push({ quote, lines: [content] });
  }
  return (
    <Box alignSelf="flex-end" maxWidth="70%" backgroundColor={colors.surface.questions} padding="10px 16px"
      borderRadius="16px 16px 2px 16px" fontFamily="Outfit" fontSize="15px" lineHeight="1.5" color={colors.text.body}>
      {blocks.filter((block) => block.lines.join("").trim()).map((block, index) => block.quote ? (
        <Box key={index} as="blockquote" margin={index ? "8px 0 0" : "0"} paddingLeft="10px" fontStyle="italic" opacity={0.8}
          borderLeft={`3px solid ${colors.primaryAction.default}`} whiteSpace="pre-wrap" noOfLines={6}>
          {block.lines.join("\n").trim()}
        </Box>
      ) : (
        <Box key={index} marginTop={index ? "12px" : "0"} whiteSpace="pre-wrap">{block.lines.join("\n").trim()}</Box>
      ))}
    </Box>
  );
}

/**
 * What the side panel looks like in the instant before its code is ready: the same frame, tabs,
 * the learner's message (or the passage they are asking about) and the composer. Never a blank
 * space where the panel is about to be. It is replaced as soon as the real panel renders.
 */
export default function SidePanelShell({ parameters }: { parameters: ChatSidePanelProps | null }) {
  const { t } = useTranslate();
  const isHeaderFixed = pageStore((state) => state.isHeaderFixed);
  const user = userStore((state) => state.currentUser);
  const references = useChatQuotedReferenceStore((state) => state.references);
  const [isLandscapeTablet] = useMediaQuery("(min-width: 950px) and (max-width: 1366px) and (orientation: landscape)");
  const isChatInFullScreenMode = !(isDesktop || isLandscapeTablet);
  const headerHeight = isMobile ? PAGE_HEADER_HEIGHT_MOBILE : PAGE_HEADER_HEIGHT;
  const deckId = parameters?.summaryOutlineDeck?.id;
  const pendingReferences = deckId ? references.filter((reference) => reference.deckId === deckId) : [];
  const pendingMessage = parameters?.initialUserMessageForChat?.trim();
  const showsSource = parameters?.activeTabId === SidePanelTab.SOURCE_CONTENT_VIEWER;
  const userMessageCount = (askQuestionsChatStore.getState().chatMessages ?? []).filter((message) => message.is_message_from_user).length;
  const showsCallPill = Boolean(user?.id) && userMessageCount < 2 && !pendingMessage;
  const close = () => { void import("../../zustand/sidePanelStores").then(({ SidePanelOrchestrator }) => SidePanelOrchestrator.closePanel()); };
  const bar = (width: string, height = "12px") => (
    <Box width={width} height={height} borderRadius="6px" backgroundColor={colors.surface.secondary} animation={`${shimmer} 1.4s ease-in-out infinite`} />
  );
  return (
    <Flex data-testid="side-panel-skeleton" aria-busy="true" aria-label={t("loading", "loading...")}
      position="fixed" right="0" top={isHeaderFixed && !isChatInFullScreenMode ? headerHeight : "0px"}
      height={{ md: isHeaderFixed && !isChatInFullScreenMode ? `calc(100dvh - ${headerHeight})` : "100dvh", base: "100dvh" }}
      width={{ base: "100%", lg: isLandscapeTablet ? "405px" : "480px" }} zIndex={{ base: 1001, md: 999 }}
      direction="column" backgroundColor={colors.surface.white}
      borderLeft={{ base: "0px", lg: `1px solid ${colors.surface.border}` }} overflow="hidden">
      <Box paddingX={{ base: "16px", md: "28px" }} paddingTop={{ base: "16px", lg: "20px" }}>
        <Box as="button" type="button" aria-label={t("close", "close")} onClick={close} display="flex" padding="0" marginBottom="12px"
          background="none" border="none" cursor="pointer">
          <IconCancelX color={colors.secondaryAction.default} width={isMobile ? 14 : 16} />
        </Box>
        {parameters?.summaryOutlineDeck ? (
          <Flex height="36px" gap={{ base: "20px", lg: "24px" }} marginBottom="8px" fontFamily="Outfit" fontWeight={500} fontSize="16px">
            {[t("chat", "chat"), t("view_source", "view source")].map((label, index) => {
              const active = showsSource ? index === 1 : index === 0;
              return (
                <Flex key={label} flex={1} justifyContent="center" alignItems="center"
                  color={active ? colors.text.header : colors.secondaryAction.default}
                  borderBottom={`${active ? 2 : 1}px solid ${active ? colors.primaryAction.default : colors.surface.tertiary}`}>
                  {label}
                </Flex>
              );
            })}
          </Flex>
        ) : null}
      </Box>
      <Flex flex={1} minHeight={0} direction="column" gap="12px" paddingX={{ base: "16px", md: "28px" }} paddingTop="20px" overflow="hidden">
        {showsSource ? (
          <Box flex={1} borderRadius="12px" backgroundColor={colors.surface.background} animation={`${shimmer} 1.4s ease-in-out infinite`} />
        ) : pendingMessage ? (
          <>
            <PendingUserMessage text={pendingMessage} />
            <Flex gap="6px" paddingY="10px" aria-hidden="true">
              {[0, 1, 2].map((dot) => (
                <Box key={dot} width="7px" height="7px" borderRadius="50%" backgroundColor={colors.primaryAction.default}
                  animation={`${pulse} 1.2s ease-in-out ${dot * 0.15}s infinite`} />
              ))}
            </Flex>
          </>
        ) : (
          <Flex direction="column" gap="18px" paddingTop="24px" aria-hidden="true">
            {bar("40%", "14px")}
            {bar("78%")}{bar("52%")}{bar("70%")}{bar("84%")}
          </Flex>
        )}
      </Flex>
      {!showsSource && (
        <Box borderTop={`1px solid ${colors.surface.tertiary}`} paddingX={{ base: "16px", md: "28px" }}
          paddingTop={{ base: "16px", md: "18px" }} paddingBottom={{ base: "calc(16px + env(safe-area-inset-bottom, 0px))", md: "18px" }}>
          {showsCallPill && (
            <Flex align="center" gap="10px" height="48px" paddingLeft="18px" paddingRight="14px" marginBottom="10px" borderRadius="100px"
              backgroundColor={colors.primaryAction.light} fontFamily="Outfit" fontSize="14px" fontWeight={500} color={colors.text.header} aria-hidden="true">
              <Box as="img" src={monkeyFace} alt="" width="36px" height="36px" />
              {t("voice.entry.callMonkey", "call monkey tutor")}
            </Flex>
          )}
          {pendingReferences.length > 0 && (
            <Box marginBottom="8px" padding="10px 14px" borderRadius="12px" backgroundColor={colors.surface.questions}
              borderLeft={`3px solid ${colors.primaryAction.default}`} fontFamily="Outfit" aria-hidden="true">
              <Box fontSize="12px" fontWeight={600} color={colors.text.label} marginBottom="4px">{t("study_guide.asking_about", "asking about")}</Box>
              <Box fontSize="14px" fontStyle="italic" color={colors.text.body} noOfLines={2}>“{pendingReferences[pendingReferences.length - 1].label ?? pendingReferences[pendingReferences.length - 1].quote}”</Box>
            </Box>
          )}
          <Flex height="52px" align="center" paddingLeft="24px" paddingRight="8px" borderRadius="30px" aria-hidden="true"
            border={`1px solid ${colors.surface.tertiary}`} backgroundColor={colors.surface.white} fontFamily="Outfit" fontSize="16px" color={colors.text.placeholder}>
            <Box flex={1}>{t("ask_anything", "ask anything")}</Box>
            <Box width="34px" height="34px" borderRadius="50%" backgroundColor={colors.primaryAction.default} opacity={0.35} />
          </Flex>
        </Box>
      )}
    </Flex>
  );
}
